import { Currency } from '../bank-account/bank-account';
import { EmployeeGlAccountKey } from '../employee-account/employee-account';
import { ExpenseClaimJournalLine } from '../expense-claim/expense-claim';
import { EntityKey, OwnerReference } from '../expense-receipt/expense-receipt';
import { FundMovementAttachment } from '../fund-movement/fund-movement';

/** Mirrors Finance.Models ExpenseSettlementStatus. */
export enum ExpenseSettlementStatus {
  Draft = 1,
  Posted = 2,
}

/** Which way the cash goes — mirrors Finance.Models ExpenseSettlementDirection. */
export enum ExpenseSettlementDirection {
  /** Claims and advances cancel out: no bank movement. */
  None = 0,
  /** The company pays (fund replenishment, or the employee is owed). */
  Outgoing = 1,
  /** The employee refunds what the advances exceeded. */
  Incoming = 2,
}

/** A claim the settlement pays. */
export interface ExpenseSettlementClaim {
  reference: string;
  number: string;
  total: number;
  /** Outstanding when last saved; re-checked at post. */
  outstanding: number;
  appliedAmount: number;
  /** The claim's booking rate (currency → functional). */
  exchangeRate: number;
  functionalAmount: number;
}

/** A posted advance the settlement nets against the claims (trip only). */
export interface ExpenseSettlementAdvance {
  reference: string;
  number: string;
  amount: number;
  outstanding: number;
  appliedAmount: number;
  exchangeRate: number;
  functionalAmount: number;
}

export interface ExpenseSettlementStatusHistoryEntry {
  date: string;
  status: ExpenseSettlementStatus;
  user: { username: string | null; firstName: string | null; lastName: string | null } | null;
  comment: string | null;
}

/** An expense settlement (Liquidación, #5799) — mirrors Finance.Models ExpenseSettlementDto. */
export interface ExpenseSettlement {
  id: string;
  /** e.g. NT-SETL-2026-000001 */
  number: string;
  financialCompanyReference: string;
  /** The fund (70) or trip (66). */
  owner: EntityKey;
  ownerName: string;
  currency: Currency;
  claims: ExpenseSettlementClaim[];
  advances: ExpenseSettlementAdvance[];
  claimsTotal: number;
  advancesTotal: number;
  /** Claims less advances: positive pays out, negative is the employee's refund. */
  net: number;
  direction: ExpenseSettlementDirection;
  settlementDate: string;
  bankAccountReference: string | null;
  bankingReference: string | null;
  transferFee: number;
  securityTax: number;
  note: string | null;
  attachments: FundMovementAttachment[];
  exchangeRate: number;
  rateToUsd: number | null;
  functionalCurrency: Currency;
  functionalAmount: number | null;
  amountUsd: number | null;
  bankGlAccount: EmployeeGlAccountKey | null;
  journalEntryReference: string | null;
  journalEntryNumber: string | null;
  bankTransactionReference: string | null;
  bankTransactionNumber: string | null;
  status: ExpenseSettlementStatus;
  statusHistory: ExpenseSettlementStatusHistoryEntry[] | null;
  createdBy: string | null;
  createdDate: string | null;
}

export interface ExpenseSettlementApplicationUpdate {
  reference: string;
  appliedAmount: number;
}

/** What the treasurer sends for a Draft settlement. */
export interface ExpenseSettlementUpdate {
  claims: ExpenseSettlementApplicationUpdate[];
  advances: ExpenseSettlementApplicationUpdate[];
  /** yyyy-MM-dd */
  settlementDate: string;
  bankAccountReference: string | null;
  bankingReference: string | null;
  transferFee: number;
  securityTax: number;
  note: string | null;
}

/** An owner's posted claims (and a trip's posted advances) with something outstanding. */
export interface ExpenseSettleable {
  currency: Currency;
  claims: ExpenseSettlementClaim[];
  advances: ExpenseSettlementAdvance[];
}

/** A fund or trip with posted claims still to settle — the treasurer's queue. */
export interface ExpenseSettlementOwner {
  owner: EntityKey;
  name: string;
  currency: Currency;
  claims: number;
  outstanding: number;
}

export interface ExpenseSettlementSearchCommand {
  search?: string | null;
  statuses?: ExpenseSettlementStatus[] | null;
  /** PettyCashFund (70) or Trip (66); null = both. */
  ownerCode?: number | null;
  ownerReference?: string | null;
}

export interface ExpenseSettlementCreateCommand {
  owner: OwnerReference;
}

/** The journal entry posting would make, and what still blocks it. */
export interface ExpenseSettlementJournalPreview {
  currency: Currency;
  lines: ExpenseClaimJournalLine[];
  totalDebit: number;
  totalCredit: number;
  missing: string[];
  isReady: boolean;
}
